import React, { useState, useRef, useEffect } from "react";
import scrollIntoViewIfNeeded from "scroll-into-view-if-needed";
import cx from "classnames";
import { useResponsive } from "@/hooks";
import Dialog, { DialogContent, DialogTitle } from "@/components/Dialog";
import { useSwipeable } from "react-swipeable";
import { CalendarIcon, FlagIcon, ChevronIcon } from "@/components/Icon";
import Button from "@/components/Button";
import styles from "./ItineraryDetails.module.scss";
import Typography from "../Typography";

interface ItineraryItem {
  title: string;
  content: string;
  duration?: number | null;
  teaser?: string | null;
  type?: string | null;
}

interface ScrollableTabsAndCardsProps {
  itinerary: ItineraryItem[];
  onItinerarySelected: (itinerary: number) => void;
  selectedIndex: number;
}

const ITINERARY_EXCERPT_CHARACTER_LIMIT = 350;

const isWordCharacter = (character: string) => /[A-Za-z0-9]/.test(character);

const truncateItineraryPreview = (text: string) => {
  const excerpt = text.slice(0, ITINERARY_EXCERPT_CHARACTER_LIMIT).trimEnd();
  const previousCharacter = excerpt.charAt(excerpt.length - 1);
  const nextCharacter = text.charAt(excerpt.length);

  if (!isWordCharacter(previousCharacter) || !isWordCharacter(nextCharacter)) {
    return excerpt;
  }

  const lastWordBoundaryIndex = excerpt.lastIndexOf(" ");

  if (lastWordBoundaryIndex <= 0) {
    return excerpt;
  }

  return excerpt.slice(0, lastWordBoundaryIndex).trimEnd();
};

const getExcerptText = (content: string) => {
  const div = document.createElement("div");
  div.innerHTML = content;
  return (div.textContent || div.innerText || "").replace(/\s+/g, " ").trim();
};

const getItineraryPreview = (content: string) => {
  const text = getExcerptText(content);

  if (text.length <= ITINERARY_EXCERPT_CHARACTER_LIMIT) {
    return {
      isTruncated: false,
      text,
    };
  }

  return {
    isTruncated: true,
    text: `${truncateItineraryPreview(text)} ...`,
  };
};

const ScrollableTabsAndCards: React.FC<ScrollableTabsAndCardsProps> = ({
  itinerary,
  onItinerarySelected,
  selectedIndex,
}) => {
  const [activeTab, setActiveTab] = useState<number>(selectedIndex);
  const [isReadMoreModalOpen, setIsReadMoreModalOpen] = useState(false);
  const [readMoreModalTab, setReadMoreModalTab] =
    useState<number>(selectedIndex);

  const selectedCardRef = useRef<HTMLDivElement | null>(null);
  const selectedTabRef = useRef<HTMLDivElement | null>(null);
  const tabContainerRef = useRef<HTMLDivElement | null>(null);
  const cardContainerRef = useRef<HTMLDivElement | null>(null);

  const { isMobile } = useResponsive();

  const handlers = useSwipeable({
    onSwipedLeft: () => nextTabHandler(),
    onSwipedRight: () => previousTabHandler(),
    preventScrollOnSwipe: true,
    trackTouch: true,
  });

  const nextTabHandler = () => {
    if (activeTab + 1 <= itinerary.length - 1) {
      onItinerarySelected(activeTab + 1);
    }
  };

  const previousTabHandler = () => {
    if (activeTab - 1 >= 0) {
      onItinerarySelected(activeTab - 1);
    }
  };

  useEffect(() => {
    const preventHorizontalScroll = (e: WheelEvent) => {
      // Prevent horizontal scroll only
      if (e.deltaX !== 0) {
        e.preventDefault();
      }
    };

    // Adding the event listener to prevent horizontal scroll on the card container
    const cardContainer = cardContainerRef.current;
    cardContainer?.addEventListener("wheel", preventHorizontalScroll, {
      passive: false,
    });

    if (selectedCardRef.current && cardContainerRef.current) {
      scrollIntoViewIfNeeded(selectedCardRef.current, {
        scrollMode: "always",
        block: "nearest",
        boundary: cardContainerRef.current,
        behavior: "smooth",
        inline: "start",
      });
    }

    // Scroll the active tab into center view
    if (selectedTabRef.current && tabContainerRef.current) {
      scrollIntoViewIfNeeded(selectedTabRef.current, {
        scrollMode: "always",
        block: "center",
        boundary: tabContainerRef.current,
        behavior: "smooth",
        inline: "center",
      });
    }

    // Cleanup: Remove event listener on component unmount
    return () => {
      cardContainer?.removeEventListener("wheel", preventHorizontalScroll);
    };
  }, [activeTab]);

  const handleTabClick = (index: number) => {
    setActiveTab(index);
    onItinerarySelected(index);
  };

  const handleCardClick = (index: number) => {
    handleTabClick(index);
  };

  const formatDuration = (duration?: number | null) => {
    if (!duration) return null;

    return `${duration} day${duration === 1 ? "" : "s"}`;
  };

  const selectReadMoreModalDay = (index: number) => {
    if (index < 0 || index >= itinerary.length) {
      return;
    }

    setReadMoreModalTab(index);
  };

  const readMoreModalDay = Math.min(
    readMoreModalTab,
    Math.max(itinerary.length - 1, 0)
  );
  const canGoToPreviousReadMoreDay = readMoreModalDay > 0;
  const canGoToNextReadMoreDay = readMoreModalDay < itinerary.length - 1;

  useEffect(() => {
    setActiveTab(selectedIndex);
  }, [selectedIndex]);

  useEffect(() => {
    if (!isReadMoreModalOpen) {
      setReadMoreModalTab(selectedIndex);
    }
  }, [isReadMoreModalOpen, selectedIndex]);

  return (
    <div {...handlers} className={styles.root}>
      <Dialog
        open={isReadMoreModalOpen}
        onClose={() => setIsReadMoreModalOpen(false)}
      >
        <DialogTitle
          hasCloseButton
          onClose={() => setIsReadMoreModalOpen(false)}
        />
        <DialogContent className={styles.dialogRootContent}>
          <Typography variant="h6">{itinerary[activeTab]?.title}</Typography>
          <div
            className={styles.dialogContent}
            dangerouslySetInnerHTML={{
              __html: itinerary[readMoreModalDay]?.content,
            }}
          />
          {itinerary.length > 1 && (
            <div className={styles.dialogButtonContainer}>
              <Button
                type="button"
                className={styles.dialogChevronIcon}
                disabled={!canGoToPreviousReadMoreDay}
                onClick={() => selectReadMoreModalDay(readMoreModalDay - 1)}
                startIcon={<ChevronIcon dir="left" />}
                variant="text"
              >
                Previous day
              </Button>
              <Typography
                color="text.secondary"
                className={styles.dayText}
                variant="caption"
              >
                Day {readMoreModalDay + 1} of {itinerary.length}
              </Typography>
              <Button
                type="button"
                className={styles.dialogChevronIcon}
                disabled={!canGoToNextReadMoreDay}
                onClick={() => selectReadMoreModalDay(readMoreModalDay + 1)}
                endIcon={<ChevronIcon dir="right" />}
                variant="text"
              >
                Next day
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <div ref={tabContainerRef} className={styles.tabContainer}>
        {itinerary.map((item, index) => (
          <div key={index} className={styles.itineraryTab}>
            <div className={styles.itineraryTabContainer}>
              <div
                className={cx(styles.itineraryTabItem, {
                  [styles.itineraryActiveTabItem]: index === activeTab,
                  [styles.itineraryPreviousTabItem]: index < activeTab,
                })}
                onClick={() => handleTabClick(index)}
                ref={(el) => {
                  if (index === activeTab) {
                    selectedTabRef.current = el;
                  }
                }}
              >
                {item.title}
              </div>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="19"
                height="10"
                viewBox="0 0 19 10"
                fill="none"
                className={cx(styles.tabIndicator, {
                  [styles.activeTabIndicator]: index === activeTab,
                })}
              >
                <path d="M9.5 10L18.5 0H0.5L9.5 10Z" fill="#00bce4" />
              </svg>
            </div>
            <svg
              width="58"
              height="8"
              viewBox="0 0 58 8"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className={cx(styles.tabIcon, {
                [styles.hiddenTabIcon]: index === itinerary.length - 1,
              })}
            >
              <path
                d="M0 3H2V5H0V3Z"
                fill={index < activeTab ? "#00bce4" : "#DDDDDD"}
              />
              <path
                d="M4 3H6V5H4V3Z"
                fill={index < activeTab ? "#00bce4" : "#DDDDDD"}
              />
              <path
                d="M8 3H10V5H8V3Z"
                fill={index < activeTab ? "#00bce4" : "#DDDDDD"}
              />
              <path
                d="M12 3H14V5H12V3Z"
                fill={index < activeTab ? "#00bce4" : "#DDDDDD"}
              />
              <path
                d="M16 3H18V5H16V3Z"
                fill={index < activeTab ? "#00bce4" : "#DDDDDD"}
              />
              <path
                d="M40 3H42V5H40V3Z"
                fill={index < activeTab ? "#00bce4" : "#DDDDDD"}
              />
              <path
                d="M44 3H46V5H44V3Z"
                fill={index < activeTab ? "#00bce4" : "#DDDDDD"}
              />
              <path
                d="M48 3H50V5H48V3Z"
                fill={index < activeTab ? "#00bce4" : "#DDDDDD"}
              />
              <path
                d="M52 3H54V5H52V3Z"
                fill={index < activeTab ? "#00bce4" : "#DDDDDD"}
              />
              <path
                d="M56 3H58V5H56V3Z"
                fill={index < activeTab ? "#00bce4" : "#DDDDDD"}
              />
              <circle
                cx="29"
                cy="4"
                r="4"
                fill={index < activeTab ? "#00bce4" : "#DDDDDD"}
              />
            </svg>
          </div>
        ))}
      </div>
      <div ref={cardContainerRef} className={styles.cardRoot}>
        {itinerary.map((item, index) => {
          const duration = formatDuration(item.duration);
          // const excerptText = getExcerptText(item.content);
          const title = getExcerptText(item.title);

          const { isTruncated, text: excerptText } = getItineraryPreview(
            item.content
          );

          return (
            <div
              key={index}
              className={cx(styles.cardContainer, {
                [styles.activeCardContainer]: index === activeTab,
              })}
              ref={(el) => {
                if (index === activeTab) {
                  selectedCardRef.current = el;
                }
              }}
              onClick={() => handleCardClick(index)}
            >
              <Typography variant="h6" className={styles.cardTitle}>
                {item.title}
              </Typography>
              {(item.type || duration) && (
                <div className={styles.cardTopContainer}>
                  {item.type && (
                    <span className={styles.cardItemContainer}>
                      <FlagIcon className={styles.cardItemIcon} />
                      {item.type}
                    </span>
                  )}
                  {duration && (
                    <span className={styles.cardItemContainer}>
                      <CalendarIcon
                        variant="outlined"
                        className={styles.cardItemIcon}
                      />
                      {duration}
                    </span>
                  )}
                </div>
              )}
              <Typography variant="body1" className={styles.cardText}>
                {excerptText}
              </Typography>
              {isTruncated && (
                <Button
                  className={styles.button}
                  aria-label={`See more about ${title}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    setReadMoreModalTab(index);
                    setIsReadMoreModalOpen(true);
                  }}
                  variant="text"
                >
                  See more
                </Button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ScrollableTabsAndCards;
